import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
  readonly titleKey: string;
  readonly milestone: string;
}

interface HealthResponse {
  status: string;
  checks: { database: { status: string }; migrations: { status: string; latest?: string } };
}

/**
 * 里程碑占位页。同时充当 M0 的端到端连通性证明：它真的调用后端 /health，
 * 因此 Vite 代理、TanStack Query、错误处理链路在 M0 就是活的，而不是等到 M1 才验证。
 */
export function PlaceholderPage({ titleKey, milestone }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const health = useQuery({
    queryKey: ['health'],
    queryFn: async (): Promise<HealthResponse> => {
      const res = await fetch('/health');
      if (!res.ok) throw Object.assign(new Error('health check failed'), { status: res.status });
      return res.json() as Promise<HealthResponse>;
    },
  });

  return (
    <section>
      <h1 style={{ margin: 0, fontSize: 'var(--text-2xl)' }}>{t(titleKey)}</h1>
      <p style={{ color: 'var(--color-text-muted)' }}>{t('shell.plannedFor', { milestone })}</p>

      <div
        style={{
          marginTop: 'var(--space-6)',
          padding: 'var(--space-4)',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--elev-1)',
          maxWidth: 420,
        }}
      >
        <strong style={{ fontSize: 'var(--text-sm)' }}>API</strong>
        <div
          style={{
            marginTop: 'var(--space-2)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {health.isPending && (
            <span style={{ color: 'var(--color-text-subtle)' }}>{t('app.loading')}</span>
          )}
          {health.isError && <span style={{ color: 'var(--color-danger)' }}>unreachable</span>}
          {health.data && (
            <>
              <div>status: {health.data.status}</div>
              <div>database: {health.data.checks.database.status}</div>
              <div>migrations: {health.data.checks.migrations.latest ?? '—'}</div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
